import React, { useState } from 'react';
import { usePersonas } from '../hooks/usePersonas';
import { Card, Button, Badge } from '@/components/ui';
import { Plus, Edit2, Trash2, Bot, Loader2, Play, Pause } from 'lucide-react';
import { cn } from '@/utils/cn';

import { TrainingSandboxDrawer } from './TrainingSandboxDrawer';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AIPersona } from '../types/persona.type';

export function PersonasTab() {
  const { personas, isLoading, isMutating, deletePersona, toggleStatus, seedDefaultPersonas } = usePersonas();
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<AIPersona | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Danh sách Bot</h3>
          <p className="text-xs text-slate-500">Quản lý tính cách và chỉ thị (Prompt) cho từng Bot.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="h-10 px-4 rounded-xl shadow-sm text-slate-700 border-slate-200 hover:bg-slate-50 font-medium"
            onClick={() => setConfirmSyncOpen(true)}
            disabled={isMutating}
          >
            {isMutating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Bot size={16} className="mr-2 text-emerald-600" />}
            Khôi phục mặc định Bot
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold h-10 px-4 rounded-xl shadow-sm"
            onClick={() => {
              setSelectedPersona(null);
              setSandboxOpen(true);
            }}
          >
            <Plus size={16} />
            Thêm Bot mới
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {personas.map((persona) => (
          <Card key={persona.id} className={cn(
            "p-5 relative overflow-hidden group transition-all duration-300",
            persona.is_active ? "border-emerald-100 hover:border-emerald-300 shadow-sm hover:shadow-md" : "border-slate-200 bg-slate-50 opacity-75"
          )}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-3 rounded-2xl text-white shadow-md",
                  persona.is_active ? "bg-gradient-to-br from-emerald-400 to-teal-600" : "bg-gradient-to-br from-slate-400 to-slate-600"
                )}>
                  <i className={cn(persona.icon || 'ri-robot-line', "text-xl")} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">{persona.name}</h4>
                  <p className="text-[10px] font-mono text-slate-500 uppercase">{persona.code}</p>
                </div>
              </div>
              <Badge 
                variant={persona.is_active ? 'premium-emerald' : 'premium-rose'}
                className="px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border-0 whitespace-nowrap shrink-0 transition-all hover:scale-105 active:scale-95 cursor-default"
              >
                {persona.is_active ? 'Hoạt động' : 'Ngưng hoạt động'}
              </Badge>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 mb-5">
              <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed italic">
                "{persona.instruction}"
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 px-3 text-xs font-bold transition-all",
                  persona.is_active 
                    ? "!border-amber-200 !bg-amber-50 !text-amber-700 hover:!bg-amber-100 hover:!border-amber-300" 
                    : "!border-emerald-200 !bg-emerald-50 !text-emerald-700 hover:!bg-emerald-100 hover:!border-emerald-300"
                )}
                onClick={() => toggleStatus({ id: persona.id, currentStatus: persona.is_active })}
              >
                {persona.is_active ? <i className="ri-pause-line mr-1.5 text-sm" /> : <i className="ri-play-line mr-1.5 text-sm" />}
                {persona.is_active ? 'Tạm dừng' : 'Kích hoạt'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs font-bold !border-blue-200 !text-blue-700 !bg-blue-50 hover:!bg-blue-100 hover:!border-blue-300 transition-all"
                onClick={() => {
                  setSelectedPersona(persona);
                  setSandboxOpen(true);
                }}
              >
                <i className="ri-edit-2-line mr-1.5 text-sm" />
                Huấn luyện
              </Button>
              <Button
                variant="outline"
                size="sm"
                title="Xóa Bot"
                className="h-8 w-8 flex items-center justify-center p-0 !border-rose-200 !text-rose-600 !bg-rose-50 hover:!bg-rose-100 hover:!border-rose-300 transition-all"
                onClick={() => setConfirmDeleteId(persona.id)}
              >
                <i className="ri-delete-bin-line text-[15px]" />
              </Button>
            </div>
          </Card>
        ))}
        {personas.length === 0 && (
          <div className="col-span-full py-16 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-slate-400" />
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">Chưa có dữ liệu Bot</h4>
            <p className="text-sm text-slate-500 max-w-md mb-6">
              Hệ thống của bạn hiện chưa được cấu hình Đội ngũ Bot. Bạn có thể tự tạo mới hoặc nạp nhanh bộ Bot chuẩn của Landscape.
            </p>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2 font-bold h-11 px-6 rounded-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
              onClick={() => seedDefaultPersonas()}
              disabled={isMutating}
            >
              {isMutating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              Nạp bộ Bot Chuẩn (15 Bot)
            </Button>
          </div>
        )}
      </div>

      {sandboxOpen && (
        <TrainingSandboxDrawer
          isOpen={sandboxOpen}
          onClose={() => setSandboxOpen(false)}
          persona={selectedPersona}
        />
      )}

      <ConfirmDialog
        isOpen={confirmSyncOpen}
        title="Cảnh báo Ghi đè Dữ liệu"
        message={
          <span className="text-slate-600 leading-relaxed block">
            Hệ thống sẽ tải lại cấu hình chuẩn cho <strong>15 Bot AI</strong>. <br /><br />
            ⚠️ <span className="text-rose-600 font-medium">Toàn bộ những thay đổi hoặc tinh chỉnh thủ công của bạn trước đó sẽ bị xóa vĩnh viễn.</span> Bạn đã suy nghĩ kỹ chưa?
          </span>
        }
        confirmText="Đồng ý Ghi đè"
        isDestructive={true}
        onClose={() => setConfirmSyncOpen(false)}
        onConfirm={async () => {
          await seedDefaultPersonas();
          setConfirmSyncOpen(false);
        }}
        isLoading={isMutating}
      />

      <ConfirmDialog
        isOpen={!!confirmDeleteId}
        title="Xóa Bot"
        message="Hành động này sẽ xóa Bot vĩnh viễn và không thể khôi phục. Bạn có chắc chắn?"
        confirmText="Xóa"
        isDestructive={true}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (confirmDeleteId) {
            await deletePersona(confirmDeleteId);
            setConfirmDeleteId(null);
          }
        }}
        isLoading={isMutating}
      />
    </div>
  );
}
